// pitchYawWaylines.ts
import { create } from 'xmlbuilder2'
import fs from 'fs'
import { RoutePoint } from '../types/route-bearing'
import { CameraAngle } from '../types/camera-angle'

// --------------------------------------------------
// Types
// --------------------------------------------------

export interface Point {
  lon: number;
  lat: number;
  height?: number;
  speed?: number;
  heading?: number;
}

export interface ObliquePoint {
  pitch: number;
  yaw: number;
  stayTime?: number;
}

export interface GeneratorOptions {
  height?: number;
  speed?: number;
  /** Main oblique angle (e.g. 40 or 45). Used by the sequence generator */
  obliqueAngle?: CameraAngle;
  /** Optional fully custom sequence (overrides obliqueAngle) */
  sequence?: ObliquePoint[];
  defaultStayTime?: number;
  droneEnumValue?: number;
  droneSubEnumValue?: number;
  payloadEnumValue?: number;
  payloadSubEnumValue?: number;
  globalTransitionalSpeed?: number;
  takeOffSecurityHeight?: number;
  executeHeightMode?:string;
}

// --------------------------------------------------
// Sequence Generator (Pitch + Yaw)
// --------------------------------------------------

/**
 * Generate a classic Pitch + Yaw Smart Oblique sequence
 * from a single oblique angle.
 *
 * Example (obliqueAngle = 40):
 *   -90° / 0°
 *   -70° / -40°
 *   -70° / +40°
 */
export function generatePitchYawSequence (
  obliqueAngle: CameraAngle = { pitch: 20, roll: 0, yaw: 40 },
  options?: {
    nadirPitch?: number;
    stayTime?: number;
  }
): ObliquePoint[] {
  const nadirPitch = options?.nadirPitch ?? -90
  const obliqueYaw = obliqueAngle.yaw ?? 40
  const stay = options?.stayTime ?? 4.2
  const obliquePitch = -90 + obliqueAngle.pitch

  return [
    { pitch: nadirPitch, yaw: 0, stayTime: 0 },
    { pitch: obliquePitch, yaw: -obliqueYaw, stayTime: 0 },
    { pitch: obliquePitch, yaw: obliqueYaw, stayTime: stay },
  ]
}

// --------------------------------------------------
// Main Generator
// --------------------------------------------------

export function generatePitchYawWaylines (
  routePoints: RoutePoint[],
  options: GeneratorOptions = {}
): string {
  if (routePoints.length < 2) {
    throw new Error('At least 2 points are required')
  }

  const {
    height = 8,
    speed = 1.5,
    obliqueAngle = { pitch: 20, roll: 0, yaw: 40 },
    sequence: customSequence,
    defaultStayTime = 0,
    droneEnumValue = 100,
    droneSubEnumValue = 0,
    payloadEnumValue = 98,
    payloadSubEnumValue = 0,
    globalTransitionalSpeed = 15,
    takeOffSecurityHeight = 20,
    executeHeightMode = 'relativeToStartPoint',
  } = options

  const sequence =
    customSequence ??
    generatePitchYawSequence(obliqueAngle, { stayTime: 4.2 })

  // Root document
  const root = create({ version: '1.0', encoding: 'UTF-8' }).ele('kml', {
    xmlns: 'http://www.opengis.net/kml/2.2',
    'xmlns:wpml': 'http://www.dji.com/wpmz/1.0.6',
  })

  const doc = root.ele('Document')

  // ---------- missionConfig ----------
  const missionConfig = doc.ele('wpml:missionConfig')
  missionConfig.ele('wpml:flyToWaylineMode').txt('safely')
  missionConfig.ele('wpml:finishAction').txt('goHome')
  missionConfig.ele('wpml:exitOnRCLost').txt('executeLostAction')
  missionConfig.ele('wpml:executeRCLostAction').txt('goBack')
  missionConfig.ele('wpml:takeOffSecurityHeight').txt(String(takeOffSecurityHeight))
  missionConfig.ele('wpml:globalTransitionalSpeed').txt(String(globalTransitionalSpeed))

  const droneInfo = missionConfig.ele('wpml:droneInfo')
  droneInfo.ele('wpml:droneEnumValue').txt(String(droneEnumValue))
  droneInfo.ele('wpml:droneSubEnumValue').txt(String(droneSubEnumValue))

  missionConfig.ele('wpml:waylineAvoidLimitAreaMode').txt('1')

  const payloadInfo = missionConfig.ele('wpml:payloadInfo')
  payloadInfo.ele('wpml:payloadEnumValue').txt(String(payloadEnumValue))
  payloadInfo.ele('wpml:payloadSubEnumValue').txt(String(payloadSubEnumValue))
  payloadInfo.ele('wpml:payloadPositionIndex').txt('0')

  // ---------- Folder ----------
  const folder = doc.ele('Folder')
  folder.ele('wpml:templateId').txt('0')
  folder.ele('wpml:executeHeightMode').txt(executeHeightMode)
  folder.ele('wpml:waylineId').txt('0')
  folder.ele('wpml:autoFlightSpeed').txt(String(speed))

  // startActionGroup
  const startAG = folder.ele('wpml:startActionGroup')

  // gimbalRotate to -90
  const a0 = startAG.ele('wpml:action')
  a0.ele('wpml:actionId').txt('0')
  a0.ele('wpml:actionActuatorFunc').txt('gimbalRotate')
  const a0p = a0.ele('wpml:actionActuatorFuncParam')
  a0p.ele('wpml:gimbalHeadingYawBase').txt('aircraft')
  a0p.ele('wpml:gimbalRotateMode').txt('absoluteAngle')
  a0p.ele('wpml:gimbalPitchRotateEnable').txt('1')
  a0p.ele('wpml:gimbalPitchRotateAngle').txt('-90')
  a0p.ele('wpml:gimbalRollRotateEnable').txt('0')
  a0p.ele('wpml:gimbalRollRotateAngle').txt('0')
  a0p.ele('wpml:gimbalYawRotateEnable').txt('1')
  a0p.ele('wpml:gimbalYawRotateAngle').txt('0')
  a0p.ele('wpml:gimbalRotateTimeEnable').txt('0')
  a0p.ele('wpml:gimbalRotateTime').txt('10')
  a0p.ele('wpml:payloadPositionIndex').txt('0')

  // hover 0.5s
  const a1 = startAG.ele('wpml:action')
  a1.ele('wpml:actionId').txt('1')
  a1.ele('wpml:actionActuatorFunc').txt('hover')
  a1.ele('wpml:actionActuatorFuncParam').ele('wpml:hoverTime').txt('0.5')

  // setFocusType
  const a2 = startAG.ele('wpml:action')
  a2.ele('wpml:actionId').txt('2')
  a2.ele('wpml:actionActuatorFunc').txt('setFocusType')
  const a2p = a2.ele('wpml:actionActuatorFuncParam')
  a2p.ele('wpml:cameraFocusType').txt('manual')
  a2p.ele('wpml:payloadPositionIndex').txt('0')

  // focus (infinite)
  const a3 = startAG.ele('wpml:action')
  a3.ele('wpml:actionId').txt('3')
  a3.ele('wpml:actionActuatorFunc').txt('focus')
  const a3p = a3.ele('wpml:actionActuatorFuncParam')
  a3p.ele('wpml:focusX').txt('0')
  a3p.ele('wpml:focusY').txt('0')
  a3p.ele('wpml:focusRegionWidth').txt('0')
  a3p.ele('wpml:focusRegionHeight').txt('0')
  a3p.ele('wpml:isPointFocus').txt('0')
  a3p.ele('wpml:isInfiniteFocus').txt('1')
  a3p.ele('wpml:payloadPositionIndex').txt('0')
  a3p.ele('wpml:isCalibrationFocus').txt('0')

  // hover 1s
  const a4 = startAG.ele('wpml:action')
  a4.ele('wpml:actionId').txt('4')
  a4.ele('wpml:actionActuatorFunc').txt('hover')
  a4.ele('wpml:actionActuatorFuncParam').ele('wpml:hoverTime').txt('1')

  folder.ele('wpml:realTimeFollowSurfaceByFov').txt('0')

  // ---------- Placemarks ----------
  let actionGroupId = 0

  routePoints.forEach((p, i) => {
    const h = p.altitude ?? height
    const s = p.speed ?? speed
    const heading = p.bearing ?? 0
    const isFirst = i === 0
    const isLast = i === routePoints.length - 1

    const pm = folder.ele('Placemark')

    const lon = p.lonlat[0]
    const lat = p.lonlat[1]

    pm.ele('Point').ele('coordinates').txt(`${lon},${lat}`)

    pm.ele('wpml:index').txt(String(i))
    pm.ele('wpml:executeHeight').txt(String(h))
    pm.ele('wpml:waypointSpeed').txt(String(s))

    const headingParam = pm.ele('wpml:waypointHeadingParam')
    headingParam.ele('wpml:waypointHeadingMode').txt('followWayline')
    headingParam.ele('wpml:waypointHeadingAngle').txt(String(heading))
    headingParam.ele('wpml:waypointPoiPoint').txt('0.000000,0.000000,0.000000')
    headingParam.ele('wpml:waypointHeadingAngleEnable').txt('1')
    headingParam.ele('wpml:waypointHeadingPathMode').txt('followBadArc')
    headingParam.ele('wpml:waypointHeadingPoiIndex').txt('0')

    const turnParam = pm.ele('wpml:waypointTurnParam')
    const turnDamping = p.turnDamping

    turnParam
      .ele('wpml:waypointTurnMode')
      .txt(
        isFirst || isLast
          ? 'toPointAndStopWithDiscontinuityCurvature'
          : 'coordinateTurn'
      )
    turnParam.ele('wpml:waypointTurnDampingDist').txt(String(turnDamping))

    pm.ele('wpml:useStraightLine').txt('1')

    // ----- Action Groups -----
    if (isFirst) {
      const ag = pm.ele('wpml:actionGroup')
      ag.ele('wpml:actionGroupId').txt(String(actionGroupId++))
      ag.ele('wpml:actionGroupStartIndex').txt(String(i))
      ag.ele('wpml:actionGroupEndIndex').txt(String(i + 1))
      ag.ele('wpml:actionGroupMode').txt('sequence')
      ag.ele('wpml:actionTrigger').ele('wpml:actionTriggerType').txt('betweenAdjacentPoints')

      // gimbalAngleLock
      const lock = ag.ele('wpml:action')
      lock.ele('wpml:actionId').txt('0')
      lock.ele('wpml:actionActuatorFunc').txt('gimbalAngleLock')
      lock.ele('wpml:actionActuatorFuncParam').ele('wpml:payloadPositionIndex').txt('0')

      // startSmartOblique
      const start = ag.ele('wpml:action')
      start.ele('wpml:actionId').txt('1')
      start.ele('wpml:actionActuatorFunc').txt('startSmartOblique')
      const param = start.ele('wpml:actionActuatorFuncParam')
      param.ele('wpml:smartObliqueCycleMode').txt('unlimited')
      param.ele('wpml:payloadPositionIndex').txt('0')

      sequence.forEach((pt) => {
        const sob = param.ele('wpml:smartObliquePoint')
        sob.ele('wpml:smartObliqueRunningTime').txt('0')
        sob.ele('wpml:smartObliqueStayTime').txt(String(pt.stayTime ?? defaultStayTime))
        sob.ele('wpml:smartObliqueEulerPitch').txt(String(pt.pitch))
        sob.ele('wpml:smartObliqueEulerRoll').txt('0')
        sob.ele('wpml:smartObliqueEulerYaw').txt(String(pt.yaw))
      })
    }

    if (isLast) {
      const ag = pm.ele('wpml:actionGroup')
      ag.ele('wpml:actionGroupId').txt(String(actionGroupId++))
      ag.ele('wpml:actionGroupStartIndex').txt(String(i))
      ag.ele('wpml:actionGroupEndIndex').txt(String(i))
      ag.ele('wpml:actionGroupMode').txt('sequence')
      ag.ele('wpml:actionTrigger').ele('wpml:actionTriggerType').txt('reachPoint')

      const stop = ag.ele('wpml:action')
      stop.ele('wpml:actionId').txt('0')
      stop.ele('wpml:actionActuatorFunc').txt('stopSmartOblique')
      stop.ele('wpml:actionActuatorFuncParam').ele('wpml:payloadPositionIndex').txt('0')

      const unlock = ag.ele('wpml:action')
      unlock.ele('wpml:actionId').txt('1')
      unlock.ele('wpml:actionActuatorFunc').txt('gimbalAngleUnlock')
    }

    // gimbal heading param
    const gimbal = pm.ele('wpml:waypointGimbalHeadingParam')
    gimbal.ele('wpml:waypointGimbalPitchAngle').txt('0')
    gimbal.ele('wpml:waypointGimbalRollAngle').txt('0')
    gimbal.ele('wpml:waypointGimbalYawAngle').txt('0')

    pm.ele('wpml:isRisky').txt('0')
    pm.ele('wpml:waypointWorkType').txt('0')
  })

  return root.end({ prettyPrint: true })
}

// // --------------------------------------------------
// // Example
// // --------------------------------------------------

// const points: Point[] = [
//   { lon: 128.30895797, lat: 37.25814202 },
//   { lon: 128.30920000, lat: 37.25818000 },
//   { lon: 128.30945000, lat: 37.25825000 },
//   { lon: 128.30970000, lat: 37.25832000 },
// ];

// const wpml = generatePitchYawWaylines(points, {
//   height: 6,
//   speed: 1.2,
//   obliqueAngle: 45, // ← just change this number
//   // sequence: [ ... ]  // optional fully custom sequence
//   executeHeightMode = flightParams.surfaceFollowModeEnable === true ? 'WGS84' : 'relativeToStartPoint'
// });

// fs.writeFileSync("waylines.wpml", wpml);
// console.log("waylines.wpml generated successfully");
